(function () {
  'use strict';

  const TZ = 'America/Sao_Paulo';

  function brazilParts(date = new Date()) {
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: TZ,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
      weekday: 'short',
    });
    const parts = fmt.formatToParts(date).reduce((acc, p) => {
      acc[p.type] = p.value;
      return acc;
    }, {});
    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      hour: Number(parts.hour),
      minute: Number(parts.minute),
      iso: `${parts.year}-${parts.month}-${parts.day}`,
    };
  }

  function isoFromYMD(year, month, day) {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  function addDaysISO(iso, n) {
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(Date.UTC(y, m - 1, d));
    date.setUTCDate(date.getUTCDate() + n);
    return isoFromYMD(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
  }

  function weekdayFromISO(iso) {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  }

  function parseHHMM(value) {
    if (!value || typeof value !== 'string') return null;
    const match = value.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;
    return Number(match[1]) * 60 + Number(match[2]);
  }

  function formatCepDigits(digits) {
    const clean = digits.replace(/\D/g, '').slice(0, 8);
    if (clean.length > 5) return `${clean.slice(0, 5)}-${clean.slice(5)}`;
    return clean;
  }

  function debounce(fn, ms) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), ms);
    };
  }

  async function fetchViaCep(digits) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 4000);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`, { signal: ctrl.signal });
      if (!res.ok) throw new Error(`viacep ${res.status}`);
      const data = await res.json();
      if (data.erro) return null;
      return {
        zip: `${digits.slice(0, 5)}-${digits.slice(5)}`,
        city: data.localidade || '',
        province: data.uf || '',
        address1: data.logradouro || 'S/N',
      };
    } finally {
      clearTimeout(timer);
    }
  }

  async function fetchShippingRates(address) {
    const params = new URLSearchParams();
    Object.entries(address).forEach(([k, v]) => {
      if (v !== '' && v != null) params.set(`shipping_address[${k}]`, v);
    });
    const url = `/cart/shipping_rates.json?${params.toString()}`;

    for (let i = 0; i < 12; i++) {
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (res.status === 200) {
        const data = await res.json();
        return data.shipping_rates || [];
      }
      if (res.status === 422) {
        const body = await res.json().catch(() => null);
        const err = new Error(`shipping_rates 422`);
        err.body = body;
        throw err;
      }
      if (res.status !== 202 && res.status !== 0) {
        throw new Error(`Unexpected status ${res.status}`);
      }
      await new Promise((r) => setTimeout(r, 600));
    }
    throw new Error('Timeout waiting for shipping rates');
  }

  async function updateCartAttributes(attributes) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });
    if (!res.ok) throw new Error('Failed to update cart attributes');
    return res.json();
  }

  class ShippingCalendar {
    constructor(root) {
      this.root = root;
      const configEl = root.parentElement.querySelector('[data-shipping-calendar-config]')
        || document.querySelector('[data-shipping-calendar-config]');
      if (!configEl) {
        console.warn('[shipping-calendar] config element not found');
        return;
      }
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (err) {
        console.error('[shipping-calendar] invalid config JSON', err);
        return;
      }

      this.state = {
        method: null,
        cep: '',
        cepValid: null,
        branch: null,
        date: null,
        slot: null,
      };

      this.calendarOffset = 0;
      this.cepRequestSeq = 0;
      this.saveDebounced = debounce(() => this.saveToCart(), 350);
      this._availabilityCache = {};
      this._resolvedBranch = null;

      this.cacheElements();
      this.renderStaticText();
      this.renderBranches();
      this.clearSavedAttributes();
      this.attachEvents();
      this.renderCalendar();
      this.renderSlots();
      this.update();
    }

    async clearSavedAttributes() {
      const keys = this.config.attributeKeys || {};
      const attributes = {
        [keys.method]: '',
        [keys.date]: '',
        [keys.slot]: '',
        [keys.cep]: '',
        [keys.branch]: '',
      };
      if (keys.weekday) attributes[keys.weekday] = '';
      try {
        await updateCartAttributes(attributes);
      } catch (err) {
        console.warn('[shipping-calendar] clear on load failed', err);
      }
      document.dispatchEvent(new CustomEvent('shipping-calendar:saved', { detail: {} }));
    }

    cacheElements() {
      this.els = {
        steps: this.root.querySelectorAll('[data-step]'),
        methods: this.root.querySelectorAll('[data-method]'),
        cepInput: this.root.querySelector('[data-cep-input]'),
        cepStatus: this.root.querySelector('[data-cep-status]'),
        branchGrid: this.root.querySelector('[data-branch-grid]'),
        calendarGrid: this.root.querySelector('[data-calendar-grid]'),
        calendarRange: this.root.querySelector('[data-calendar-range]'),
        calendarPrev: this.root.querySelector('[data-calendar-prev]'),
        calendarNext: this.root.querySelector('[data-calendar-next]'),
        slotGrid: this.root.querySelector('[data-slot-grid]'),
        summary: this.root.querySelector('[data-summary]'),
        summaryBody: this.root.querySelector('[data-summary-body]'),
      };
      this.els.branches = this.root.querySelectorAll('[data-branch]');
    }

    setText(selector, value) {
      const el = this.root.querySelector(selector);
      if (!el) return;
      if (value == null || value === '') {
        if (selector === '[data-subtitle]') el.style.display = 'none';
        return;
      }
      el.textContent = value;
    }

    renderStaticText() {
      const l = this.config.locale || {};
      const labels = this.config.methodLabels || {};
      this.setText('[data-eyebrow]', l.eyebrow);
      this.setText('[data-title]', l.title);
      this.setText('[data-subtitle]', l.subtitle);
      this.setText('[data-method-step-label]', l.methodStepLabel);
      this.setText('[data-cep-step-label]', l.cepStepLabel);
      this.setText('[data-cep-lookup-label]', l.cepLookupLabel);
      this.setText('[data-cep-field-label]', l.cepFieldLabel);
      this.setText('[data-branch-step-label]', l.branchStepLabel);
      this.setText('[data-date-step-label]', l.dateStepLabel);
      this.setText('[data-slot-step-label]', l.slotStepLabel);

      this.setText('[data-method-name="motoboy"]', labels.motoboy);
      this.setText('[data-method-name="pickup"]', labels.pickup);
      this.setText('[data-method-desc="motoboy"]', l.motoboyDescription);
      this.setText('[data-method-desc="pickup"]', l.pickupDescription);

      this.setText('[data-notice-title]', l.noticeTitle);
      this.setText('[data-notice-text]', l.noticeText);

      const prevBtn = this.root.querySelector('[data-calendar-prev]');
      if (prevBtn && l.prevWeek) prevBtn.setAttribute('aria-label', l.prevWeek);
      const nextBtn = this.root.querySelector('[data-calendar-next]');
      if (nextBtn && l.nextWeek) nextBtn.setAttribute('aria-label', l.nextWeek);

      const cepInput = this.root.querySelector('[data-cep-input]');
      if (cepInput && l.cepPlaceholder) cepInput.placeholder = l.cepPlaceholder;
    }

    renderBranches() {
      if (!this.els.branchGrid) return;
      const branches = this.config.branches || [];
      this.els.branchGrid.innerHTML = '';
      branches.forEach((branch) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'shipping-calendar__branch';
        btn.dataset.branch = branch.name;
        btn.setAttribute('aria-pressed', 'false');
        btn.innerHTML = `
          <span class="shipping-calendar__branch-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s-7-7.5-7-13a7 7 0 0 1 14 0c0 5.5-7 13-7 13z"/>
              <circle cx="12" cy="9" r="2.5"/>
            </svg>
          </span>
          <span class="shipping-calendar__branch-body">
            <span class="shipping-calendar__branch-name">${branch.name || ''}</span>
            ${branch.address ? `<span class="shipping-calendar__branch-line">${branch.address}</span>` : ''}
            ${branch.hours ? `<span class="shipping-calendar__branch-line shipping-calendar__branch-line--muted">${branch.hours}</span>` : ''}
          </span>
        `;
        btn.addEventListener('click', () => this.selectBranch(branch.name));
        this.els.branchGrid.appendChild(btn);
      });
      this.els.branches = this.root.querySelectorAll('[data-branch]');
    }

    hydrateFromSaved() {
      const s = this.config.savedAttributes || {};
      const labels = this.config.methodLabels || {};
      const values = this.config.methodAttributeValues || {};
      let method = null;
      if (s.method) {
        if (s.method === values.motoboy || s.method === labels.motoboy) method = 'motoboy';
        else if (s.method === values.pickup || s.method === labels.pickup) method = 'pickup';
        else if (/retir|pickup/i.test(s.method)) method = 'pickup';
        else method = 'motoboy';
      }

      this.state.method = method;
      this.state.cep = s.cep || '';
      this.state.branch = s.branch || null;

      if (s.date && /^\d{4}-\d{2}-\d{2}$/.test(s.date)) {
        this.state.date = s.date;
      } else if (s.date && /^\d{2}\/\d{2}\/\d{4}$/.test(s.date)) {
        const [d, m, y] = s.date.split('/');
        this.state.date = `${y}-${m}-${d}`;
      }

      const allSlots = this.config.slots || [];
      const slotMatch = allSlots.find((sl) => {
        if (!s.slot) return false;
        if (sl.id === s.slot) return true;
        if (sl.label === s.slot) return true;
        if (`${sl.label} (${sl.hours})` === s.slot) return true;
        const normalizedSavedSlot = s.slot.replace(/\s*-\s*/g, ' às ');
        if (sl.hours === normalizedSavedSlot || sl.hours === s.slot) return true;
        const startHour = parseInt((s.slot.match(/^\s*(\d{1,2}):/) || [])[1], 10);
        if (!Number.isNaN(startHour)) {
          if (sl.id === 'manha' && startHour < 12) return true;
          if (sl.id === 'tarde' && startHour >= 12 && startHour < 18) return true;
          if (sl.id === 'noite' && startHour >= 18) return true;
        }
        return false;
      });
      this.state.slot = slotMatch ? slotMatch.id : null;

      if (this.els.cepInput && this.state.cep) {
        this.els.cepInput.value = formatCepDigits(this.state.cep);
        if (this.state.method === 'motoboy') {
          this.validateCep({ silent: true });
        }
      }
    }

    attachEvents() {
      this.els.methods.forEach((btn) => {
        btn.addEventListener('click', () => this.selectMethod(btn.dataset.method));
      });

      if (this.els.cepInput) {
        this.els.cepInput.addEventListener('input', (e) => {
          const formatted = formatCepDigits(e.target.value);
          e.target.value = formatted;
          this.state.cep = formatted;
          this.state.cepValid = null;
          this.setCepStatus('', null);
          if (formatted.replace(/\D/g, '').length === 8) {
            this.validateCep();
          } else {
            this.update();
          }
        });
        this.els.cepInput.addEventListener('blur', () => {
          if (this.state.cep && this.state.cepValid === null) this.validateCep();
        });
      }

      const verifyBtn = this.root.querySelector('[data-cep-verify]');
      if (verifyBtn) {
        verifyBtn.addEventListener('click', () => {
          // Sync state from the visible input. The browser may have restored
          // the input value (back navigation, autofill) without JS state
          // catching up, so we re-read it before validating.
          if (this.els.cepInput) {
            const formatted = formatCepDigits(this.els.cepInput.value);
            if (formatted !== this.state.cep) {
              this.state.cep = formatted;
              this.els.cepInput.value = formatted;
            }
          }
          if (!this.state.cep) {
            if (this.els.cepInput) this.els.cepInput.focus();
            return;
          }
          this.state.cepValid = null;
          this.validateCep();
        });
      }

      if (this.els.calendarPrev) {
        this.els.calendarPrev.addEventListener('click', () => {
          this.calendarOffset = Math.max(0, this.calendarOffset - 7);
          this.renderCalendar();
          this.refreshAvailability();
        });
      }
      if (this.els.calendarNext) {
        this.els.calendarNext.addEventListener('click', () => {
          const max = Math.max(0, this.config.calendarDays - 7);
          this.calendarOffset = Math.min(max, this.calendarOffset + 7);
          this.renderCalendar();
          this.refreshAvailability();
        });
      }
    }

    selectMethod(method) {
      if (this.state.method === method) return;
      this.state.method = method;
      // Preserve CEP, branch, and validation across method toggles — the customer
      // expects to come back to a previously-filled step without re-entering data.
      // canShowDate() reads the right field per method, so stale fields are inert.
      this.update();
      this.saveDebounced();
      this.refreshAvailability();
    }

    selectBranch(branch) {
      this.state.branch = branch;
      this._availabilityCache = {};
      // The new branch may be closed on the day already chosen; drop the stale
      // selection so the checkout gate can't stay green on a disabled date.
      if (this.state.date && this.isBranchBlocked(this.state.date)) {
        this.state.date = null;
        this.state.slot = null;
      } else if (this.state.date && this.state.slot
        && !this.isSlotAvailable(this.state.date, this.state.slot)) {
        this.state.slot = null;
      }
      this.update();
      this.saveDebounced();
      this.refreshAvailability();
    }

    selectDate(iso) {
      this.state.date = iso;
      if (this.state.slot && !this.isSlotAvailable(iso, this.state.slot)) {
        this.state.slot = null;
      }
      this.update();
      this.saveDebounced();
    }

    selectSlot(id) {
      this.state.slot = id;
      this.update();
      this.saveDebounced();
    }

    async validateCep({ silent = false } = {}) {
      const digits = (this.state.cep || '').replace(/\D/g, '');
      if (digits.length !== 8) {
        if (!silent) this.setCepStatus(this.config.locale.cepFormatError, 'invalid');
        this.state.cepValid = false;
        this.update();
        return;
      }
      const seq = ++this.cepRequestSeq;
      this.state.cepValid = null;
      if (!silent) this.setCepStatus(this.config.locale.cepChecking, 'checking');

      try {
        let enriched;
        let viaCepReached = true;
        try {
          enriched = await fetchViaCep(digits);
        } catch (err) {
          viaCepReached = false;
          enriched = null;
          console.warn('[shipping-calendar] viacep unreachable, falling back to bare zip', err);
        }
        if (seq !== this.cepRequestSeq) return;

        if (viaCepReached && enriched === null) {
          this.state.cepValid = false;
          this.setCepStatus(this.config.locale.cepNotFound, 'invalid');
          this.update();
          return;
        }

        const address = enriched
          ? { country: 'Brazil', ...enriched }
          : { country: 'Brazil', zip: formatCepDigits(digits) };

        const rates = await fetchShippingRates(address);
        if (seq !== this.cepRequestSeq) return;

        const ok = rates.length > 0;
        this.state.cepValid = ok;
        if (ok) {
          this._resolvedBranch = await this.resolveBranch(formatCepDigits(digits));
          this._availabilityCache = {};
        } else {
          this._resolvedBranch = null;
        }
        if (seq !== this.cepRequestSeq) return;
        this.setCepStatus(ok ? this.config.locale.cepValid : this.config.locale.cepInvalid, ok ? 'valid' : 'invalid');
        this.update();
        if (ok) {
          this.saveDebounced();
          this.refreshAvailability();
        }
      } catch (err) {
        if (seq !== this.cepRequestSeq) return;
        console.warn('[shipping-calendar] rate check failed', err);
        this.state.cepValid = false;
        this.setCepStatus(this.config.locale.cepNetworkError, 'invalid');
        this.update();
      }
    }

    setCepStatus(text, state) {
      if (!this.els.cepStatus) return;
      this.els.cepStatus.textContent = text || '';
      if (state) this.els.cepStatus.dataset.state = state;
      else delete this.els.cepStatus.dataset.state;
    }

    backendUrl() {
      const url = (this.config.backendUrl || '').trim().replace(/\/+$/, '');
      return url || null;
    }

    backendHeaders() {
      // ngrok free tunnels show a browser interstitial unless this header is set;
      // any value works. Harmless on non-ngrok backends.
      return { 'ngrok-skip-browser-warning': '1' };
    }

    async resolveBranch(cep) {
      const base = this.backendUrl();
      if (!base) return null;
      try {
        const res = await fetch(
          `${base}/resolve-branch?cep=${encodeURIComponent(cep)}`,
          { headers: this.backendHeaders() }
        );
        if (!res.ok) return null;
        const data = await res.json();
        return data.branch || null;
      } catch (err) {
        console.warn('[shipping-calendar] resolve-branch failed', err);
        return null;
      }
    }

    async fetchAvailability(iso, branch) {
      const base = this.backendUrl();
      if (!base || !branch) return null;
      const key = `${iso}:${branch}`;
      if (this._availabilityCache[key]) return this._availabilityCache[key];
      try {
        const url = `${base}/availability?date=${iso}&location=${encodeURIComponent(branch)}`;
        const res = await fetch(url, { headers: this.backendHeaders() });
        if (!res.ok) return null;
        const data = await res.json();
        this._availabilityCache[key] = data;
        return data;
      } catch (err) {
        console.warn('[shipping-calendar] availability fetch failed', err);
        return null;
      }
    }

    currentBranch() {
      if (this.state.method === 'pickup') return this.state.branch || null;
      if (this.state.method === 'motoboy' && this.state.cepValid === true) return this._resolvedBranch;
      return null;
    }

    async refreshAvailability() {
      const branch = this.currentBranch();
      if (!branch || !this.backendUrl()) return;
      const days = this.getCalendarDays();
      const visible = days.slice(this.calendarOffset, this.calendarOffset + 7);
      await Promise.all(visible.map((d) => this.fetchAvailability(d.iso, branch)));
      this.renderCalendar();
      this.renderSlots();
    }

    update() {
      this.els.methods.forEach((btn) => {
        btn.setAttribute('aria-pressed', String(btn.dataset.method === this.state.method));
      });
      this.els.branches.forEach((btn) => {
        btn.setAttribute('aria-pressed', String(btn.dataset.branch === this.state.branch));
      });

      this.els.steps.forEach((step) => {
        const showWhen = step.dataset.showWhen;
        if (!showWhen) return;
        const visible = showWhen === this.state.method;
        step.classList.toggle('shipping-calendar__step--hidden', !visible);
      });

      const ready = this.canShowDate();
      this.toggleStep('date', ready);
      this.toggleStep('slot', ready && !!this.state.date);

      this.renderCalendar();
      this.renderSlots();
      this.renderSummary();

      let nextStep = null;
      if (!this.state.method) {
        nextStep = 'method';
      } else if (this.state.method === 'motoboy' && this.state.cepValid !== true) {
        nextStep = 'cep';
      } else if (this.state.method === 'pickup' && !this.state.branch) {
        nextStep = 'branch';
      } else if (!this.state.date) {
        nextStep = 'date';
      } else if (!this.state.slot) {
        nextStep = 'slot';
      }
      document.dispatchEvent(new CustomEvent('shipping-calendar:ready-changed', {
        detail: { ready: nextStep === null, nextStep: nextStep },
      }));
    }

    canShowDate() {
      if (this.state.method === 'motoboy') return this.state.cepValid === true;
      if (this.state.method === 'pickup') return !!this.state.branch;
      return false;
    }

    toggleStep(name, visible) {
      const step = this.root.querySelector(`[data-step="${name}"]`);
      if (!step) return;
      step.classList.toggle('shipping-calendar__step--hidden', !visible);
    }

    getHolidaySet() {
      if (this._holidaySet) return this._holidaySet;
      const raw = this.config.holidays || [];
      const set = new Set();
      raw.forEach((entry) => {
        const value = (entry || '').trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) set.add(value);
        else if (value) console.warn('[shipping-calendar] ignoring malformed holiday entry:', entry);
      });
      this._holidaySet = set;
      return set;
    }

    isHoliday(iso) {
      return this.getHolidaySet().has(iso);
    }

    getCalendarDays() {
      const now = brazilParts();
      const startISO = addDaysISO(now.iso, this.config.leadDays || 0);
      const total = this.config.calendarDays || 14;
      const days = [];
      for (let i = 0; i < total; i++) {
        const iso = addDaysISO(startISO, i);
        const wd = weekdayFromISO(iso);
        const operational = (this.config.operationalDays || []).includes(wd)
          && !this.isHoliday(iso)
          && !this.isBranchBlocked(iso);
        days.push({ iso, weekday: wd, operational });
      }
      return days;
    }

    renderCalendar() {
      if (!this.els.calendarGrid) return;
      const days = this.getCalendarDays();
      const visible = days.slice(this.calendarOffset, this.calendarOffset + 7);
      const locale = this.config.locale;

      if (this.els.calendarRange && visible.length) {
        const first = visible[0];
        const last = visible[visible.length - 1];
        const [, fm, fd] = first.iso.split('-').map(Number);
        const [, lm, ld] = last.iso.split('-').map(Number);
        const fLabel = `${fd} ${locale.monthsShort[fm - 1]}`;
        const lLabel = `${ld} ${locale.monthsShort[lm - 1]}`;
        this.els.calendarRange.textContent = `${fLabel} — ${lLabel}`;
      }

      if (this.els.calendarPrev) this.els.calendarPrev.disabled = this.calendarOffset === 0;
      if (this.els.calendarNext) {
        const max = Math.max(0, days.length - 7);
        this.els.calendarNext.disabled = this.calendarOffset >= max;
      }

      this.els.calendarGrid.innerHTML = '';
      visible.forEach((day) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'shipping-calendar__day';
        btn.dataset.date = day.iso;
        btn.setAttribute('role', 'option');

        const [, m, d] = day.iso.split('-').map(Number);
        btn.innerHTML = `
          <span class="shipping-calendar__day-weekday">${locale.weekdaysShort[day.weekday]}</span>
          <span class="shipping-calendar__day-number">${d}</span>
          <span class="shipping-calendar__day-month">${locale.monthsShort[m - 1]}</span>
        `;

        const hasAnySlot = this.getSlotsForDate(day.iso).some((s) => this.isSlotAvailable(day.iso, s.id));
        const enabled = day.operational && hasAnySlot;

        btn.disabled = !enabled;
        btn.setAttribute('aria-pressed', String(this.state.date === day.iso));
        if (enabled) {
          btn.addEventListener('click', () => this.selectDate(day.iso));
        }
        this.els.calendarGrid.appendChild(btn);
      });
    }

    getSlotsForDate(iso) {
      const all = this.config.slots || [];
      const wd = weekdayFromISO(iso);
      const sat = this.config.saturday || {};
      if (wd === 6 && sat.morningOnly) {
        const manha = all.find((s) => s.id === 'manha');
        if (!manha) return [];
        const override = { ...manha };
        if (sat.morningHours) override.hours = sat.morningHours;
        if (sat.morningCutoff) override.cutoff = sat.morningCutoff;
        return [override];
      }
      return all;
    }

    // Per-branch weekday closures (theme editor: "Dias sem retirada nesta unidade").
    // Pickup-only by design: the branch also backs motoboy delivery via CEP routing,
    // and a closed pickup counter doesn't stop deliveries going out from it.
    isBranchBlocked(iso) {
      if (this.state.method !== 'pickup' || !this.state.branch) return false;
      const branch = (this.config.branches || []).find((b) => b.name === this.state.branch);
      return (branch?.blockedWeekdays || []).includes(weekdayFromISO(iso));
    }

    isSlotAvailable(iso, slotId) {
      const slot = this.getSlotsForDate(iso).find((s) => s.id === slotId);
      if (!slot) return false;
      const wd = weekdayFromISO(iso);
      if (!(this.config.operationalDays || []).includes(wd)) return false;
      if (this.isHoliday(iso)) return false;
      if (this.isBranchBlocked(iso)) return false;

      const now = brazilParts();
      if (iso === now.iso) {
        const cutoff = parseHHMM(slot.cutoff);
        if (cutoff !== null) {
          const nowMinutes = now.hour * 60 + now.minute;
          if (nowMinutes >= cutoff) return false;
        }
      }

      const branch = this.currentBranch();
      if (branch) {
        const cache = this._availabilityCache[`${iso}:${branch}`];
        if (cache?.slots?.[slotId]?.available === false) return false;
      }

      return true;
    }

    slotCapacityReason(iso, slotId) {
      const branch = this.currentBranch();
      if (!branch) return null;
      const cache = this._availabilityCache[`${iso}:${branch}`];
      if (cache?.slots?.[slotId]?.available === false) {
        return this.config.locale.slotFull;
      }
      return null;
    }

    slotUnavailableReason(iso, slotId) {
      const slot = this.getSlotsForDate(iso).find((s) => s.id === slotId);
      if (!slot) return null;
      const now = brazilParts();
      if (iso === now.iso) {
        const cutoff = parseHHMM(slot.cutoff);
        if (cutoff !== null && now.hour * 60 + now.minute >= cutoff) {
          return this.config.locale.slotPastCutoff;
        }
      }
      return this.slotCapacityReason(iso, slotId);
    }

    slotIconSvg(slotId) {
      const svgs = {
        manha: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="13" r="3.5"/><path d="M12 5.5V4M19 13h1.5M5 13H3.5M17 8l1-1M7 8L6 7"/><path d="M3 19h18"/></svg>`,
        tarde: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3.5"/><path d="M12 4V2M12 22v-2M4 12H2M22 12h-2M5.5 5.5L4 4M20 20l-1.5-1.5M5.5 18.5L4 20M20 4l-1.5 1.5"/></svg>`,
        noite: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`,
      };
      return svgs[slotId] || '';
    }

    renderSlots() {
      if (!this.els.slotGrid) return;
      this.els.slotGrid.innerHTML = '';
      const iso = this.state.date;
      if (!iso) return;

      this.getSlotsForDate(iso).forEach((slot) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'shipping-calendar__slot';
        btn.dataset.slot = slot.id;

        const available = this.isSlotAvailable(iso, slot.id);
        const reason = this.slotUnavailableReason(iso, slot.id);

        btn.innerHTML = `
          <span class="shipping-calendar__slot-icon" aria-hidden="true">${this.slotIconSvg(slot.id)}</span>
          <span class="shipping-calendar__slot-body">
            <span class="shipping-calendar__slot-name">${slot.label}</span>
            <span class="shipping-calendar__slot-hours">${slot.hours}</span>
            ${reason ? `<span class="shipping-calendar__slot-tag shipping-calendar__slot-tag--unavailable">${reason}</span>` : ''}
          </span>
        `;

        btn.disabled = !available;
        btn.setAttribute('aria-pressed', String(this.state.slot === slot.id));
        if (available) {
          btn.addEventListener('click', () => this.selectSlot(slot.id));
        }
        this.els.slotGrid.appendChild(btn);
      });
    }

    renderSummary() {
      if (!this.els.summary || !this.els.summaryBody) return;
      const notice = this.root.querySelector('[data-notice]');
      const setSummaryVisible = (visible) => {
        this.els.summary.classList.toggle('shipping-calendar__summary--hidden', !visible);
        this.els.summary.hidden = !visible;
      };
      const setNotice = (visible) => {
        if (!notice) return;
        notice.classList.toggle('shipping-calendar__notice--hidden', !visible);
        notice.hidden = !visible;
      };
      const ready = this.state.method && this.state.date && this.state.slot
        && (this.state.method === 'pickup' ? this.state.branch : this.state.cepValid === true);

      if (!ready) {
        setSummaryVisible(false);
        setNotice(false);
        return;
      }

      const slot = this.getSlotsForDate(this.state.date).find((s) => s.id === this.state.slot);
      if (!slot) {
        setSummaryVisible(false);
        setNotice(false);
        return;
      }
      const [, m, d] = this.state.date.split('-').map(Number);
      const wd = weekdayFromISO(this.state.date);
      const dateLabel = `${this.config.locale.weekdaysLong[wd]}, ${d} de ${this.config.locale.months[m - 1]}`;

      const intro = this.state.method === 'motoboy'
        ? this.config.locale.summaryDelivery
        : this.config.locale.summaryPickup;

      const where = this.state.method === 'motoboy'
        ? `CEP <strong>${formatCepDigits(this.state.cep)}</strong>`
        : `<strong>${this.state.branch}</strong>`;

      this.els.summaryBody.innerHTML = `
        ${intro} <strong>${dateLabel}</strong>, no período <strong>${slot.label}</strong> (${slot.hours}) — ${where}.
      `;
      setSummaryVisible(true);
      setNotice(true);
    }

    async saveToCart() {
      const keys = this.config.attributeKeys;
      const labels = this.config.methodLabels;
      const methodValues = this.config.methodAttributeValues || labels;
      const slot = this.state.date
        ? this.getSlotsForDate(this.state.date).find((s) => s.id === this.state.slot)
        : (this.config.slots || []).find((s) => s.id === this.state.slot);

      const dateLabel = (() => {
        if (!this.state.date) return '';
        const [y, m, d] = this.state.date.split('-');
        return `${d}/${m}/${y}`;
      })();

      const slotValue = slot ? (slot.hours || '').replace(/\s*às\s*/i, ' - ') : '';
      const cepDigits = (this.state.cep || '').replace(/\D/g, '');
      const branchValue = this.state.method === 'pickup'
        ? (this.state.branch || '')
        : (this.state.method === 'motoboy' ? (this._resolvedBranch || '') : '');
      const weekdayValue = this.state.date
        ? (this.config.locale.weekdaysLong[weekdayFromISO(this.state.date)] || '').toLowerCase()
        : '';

      const attributes = {
        [keys.method]: this.state.method ? (methodValues[this.state.method] || labels[this.state.method] || '') : '',
        [keys.date]: dateLabel,
        [keys.slot]: slotValue,
        [keys.cep]: this.state.method === 'motoboy' ? cepDigits : '',
        [keys.branch]: branchValue,
      };
      if (keys.weekday) attributes[keys.weekday] = weekdayValue;

      try {
        await updateCartAttributes(attributes);
        document.dispatchEvent(new CustomEvent('shipping-calendar:saved', { detail: { ...this.state } }));
      } catch (err) {
        console.warn('[shipping-calendar] save failed', err);
      }
    }
  }

  function init() {
    document.querySelectorAll('[data-shipping-calendar]').forEach((el) => {
      if (el.__shippingCalendar) return;
      el.__shippingCalendar = new ShippingCalendar(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-shipping-calendar]')) init();
  });
})();
